import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Mail, Calendar } from 'lucide-react';
import Reveal from '../components/Reveal';
import PartnersMarquee from '../components/PartnersMarquee';
import CaseStudies from '../components/CaseStudies';
import { services, images, bookingUrl, serviceImages } from '../data/mock';

const ServiceBlock = ({ s, i }) => {
  const img = serviceImages[s.id];
  const reversed = i % 2 === 1;
  return (
      <section id={s.id} className={`py-24 scroll-mt-20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#fbf9f5]'}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className={`grid md:grid-cols-2 gap-14 items-center ${reversed ? 'md:[&>*:first-child]:order-2' : ''}`}>
            <Reveal>
              <div className="relative aspect-[4/5] md:aspect-[4/4] overflow-hidden">
                <img src={img} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div>
                <div className="section-label mb-3"><span className="divider-gold"></span>{s.tagline}</div>
                <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight">{s.title}</h2>
                <p className="mt-6 text-[#010c23]/75 leading-relaxed justify-text">{s.description}</p>
                <ul className="mt-8 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[#010c23]/80">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[#ff7300]/10 flex items-center justify-center flex-shrink-0"><Check size={13} className="text-[#ff7300]" /></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex gap-4">
                  <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-outline-dark"><Calendar size={16} /> Prendre rendez-vous en ligne</a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
  );
};

const ServicesHero = () => (
  <section className="relative pt-40 pb-28 bg-[#010c23] text-white overflow-hidden">
    <div className="absolute inset-0 opacity-25">
      <img src={images.charts1} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#010c23]/60 to-[#010c23]" />
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-14 bg-[#ff7300]" />
          <span className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Nos services</span>
        </div>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">
          Une expertise <span className="italic text-[#ff7300]">360°</span> pour un patrimoine maîtrisé
        </h1>
        <p className="mt-8 text-lg text-white/85 max-w-2xl">Du bilan patrimonial à la transmission, des expertises complémentaires articulées autour d’une stratégie unique : la vôtre.</p>
      </Reveal>
      <div className="mt-14 flex flex-wrap gap-3">
        {services.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="text-xs tracking-[0.25em] uppercase border border-white/25 hover:border-[#ff7300] hover:text-[#ff7300] px-4 py-2.5 transition-colors">
            <span className="text-[#ff7300] mr-2">{s.number}</span>{s.title}
          </a>
        ))}
      </div>
    </div>
  </section>
);

const PartnersSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-10">
      <div className="section-label mb-4"><span className="divider-gold"></span>Nos partenaires</div>
      <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light max-w-3xl leading-tight">Un écosystème d’acteurs de référence</h2>
      <p className="mt-4 text-[#010c23]/60 max-w-2xl">Assureurs, sociétés de gestion et institutions financières sélectionnés pour la qualité de leurs solutions.</p>
    </div>
    <PartnersMarquee />
  </section>
);

const ServicesCta = () => (
  <section className="py-28 bg-[#fbf9f5] text-center">
    <div className="max-w-3xl mx-auto px-6">
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight">Prêt à structurer votre <span className="italic text-[#ff7300]">patrimoine</span> ?</h2>
        <p className="mt-6 text-[#010c23]/70">Un premier échange gratuit et sans engagement pour comprendre votre situation, vos projets et vos priorités.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary">Nous contacter <Mail size={18} /></Link>
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-outline-dark"><Calendar size={18} /> Prendre rendez-vous en ligne</a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <div>
    <ServicesHero />
    {services.map((s, i) => <ServiceBlock key={s.id} s={s} i={i} />)}
    <CaseStudies />
    <PartnersSection />
    <ServicesCta />
  </div>
);

export default Services;
